import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ScanLine, Camera, CameraOff, Keyboard, Loader2, CheckCircle2, X as XIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ScannedPackageDetail from "@/components/ScannedPackageDetail";
import DeliveryProofCapture from "@/components/DeliveryProofCapture";

export default function RiderScanner() {
  const { user } = useAuth();
  const [cameraOn, setCameraOn] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [result, setResult] = useState(null);
  const [proofMode, setProofMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    return () => { stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopCamera = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (e) { console.debug("scanner.stop ignored:", e); }
      try { scannerRef.current.clear(); } catch (e) { console.debug("scanner.clear ignored:", e); }
      scannerRef.current = null;
    }
    setCameraOn(false);
  };

  const startCamera = async () => {
    try {
      setCameraOn(true);
      const qr = new Html5Qrcode("rider-qr-reader");
      scannerRef.current = qr;
      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decoded) => {
          await stopCamera();
          await submitCode(decoded);
        },
        () => {}
      );
    } catch {
      toast.error("Camera access denied. Use manual entry below.");
      setCameraOn(false);
    }
  };

  const submitCode = async (code) => {
    setScanning(true);
    try {
      const { data } = await api.post("/rider/scan", { code });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScanning(false);
    }
  };

  const onManualSubmit = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    submitCode(manualCode.trim());
    setManualCode("");
  };

  const onAdvance = async () => {
    if (!result?.qr?.qr_code_id) return;
    if (result.next_status === "DELIVERED") {
      setProofMode(true);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/rider/qr/${result.qr.qr_code_id}/advance`, {});
      toast.success(`Status updated to ${prettify(data.status)}.`);
      setResult(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onProofConfirm = async (payload) => {
    if (!result?.qr?.qr_code_id) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/rider/qr/${result.qr.qr_code_id}/advance`, payload);
      toast.success(`Delivered to ${payload.receiver_name || "receiver"}. Proof saved.`);
      setProofMode(false);
      setResult(null);
      return data;
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const closeDialog = () => {
    if (submitting) return;
    setProofMode(false);
    setResult(null);
  };

  return (
    <div data-testid="rider-scanner-page" className="space-y-5">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ Scan QR</div>
        <h1 className="font-display text-2xl font-bold tracking-tight">
          Hey, {(user?.rider_name || user?.name || "Rider").split(" ")[0]}
        </h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm">Scan a package to update its status.</p>
      </div>

      <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-900/60 relative">
        <div id="rider-qr-reader" className={cameraOn ? "w-full" : "hidden"} />
        {!cameraOn && (
          <div className="aspect-square grid place-items-center text-center p-6">
            <div>
              <div className="w-20 h-20 rounded-full bg-cyan-500/10 border border-cyan-500/30 grid place-items-center mx-auto mb-4">
                <ScanLine className="w-9 h-9 text-cyan-400" />
              </div>
              <div className="font-display text-lg font-semibold mb-1">Tap to start scanning</div>
              <div className="text-xs text-slate-600 dark:text-slate-400 mb-5">Aim at the QR on the package label.</div>
              <Button
                data-testid="rider-start-camera"
                onClick={startCamera}
                disabled={scanning}
                className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium h-12 px-6"
              >
                <Camera className="w-4 h-4 mr-2" /> Open Camera
              </Button>
            </div>
          </div>
        )}
        {cameraOn && (
          <button
            data-testid="rider-stop-camera"
            onClick={stopCamera}
            className="absolute top-3 right-3 bg-white/70 dark:bg-slate-950/70 border border-slate-300 dark:border-slate-700 rounded-full px-3 py-1.5 text-xs flex items-center gap-1.5 hover:bg-slate-100 hover:dark:bg-slate-900"
          >
            <CameraOff className="w-3.5 h-3.5" /> Stop
          </button>
        )}
        {scanning && (
          <div className="absolute inset-0 grid place-items-center bg-black/70 backdrop-blur-sm">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto mb-2" />
              <div className="text-sm">Looking up…</div>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={onManualSubmit} className="border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-100/40 dark:bg-slate-900/40 p-4">
        <div className="flex items-center gap-2 mb-2 text-slate-600 dark:text-slate-400">
          <Keyboard className="w-4 h-4" />
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em]">Manual entry</div>
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="rider-manual-input"
            value={manualCode}
            onChange={e => setManualCode(e.target.value)}
            placeholder="QR-XXXXXXXX"
            className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 font-mono-tech uppercase"
          />
          <Button
            type="submit"
            data-testid="rider-manual-submit"
            disabled={scanning || !manualCode.trim()}
            className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
          >
            Look up
          </Button>
        </div>
      </form>

      <Dialog open={!!result} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" /> Package Found
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
              {result?.qr?.qr_code_id} · Next: {result?.next_action_label}
            </DialogDescription>
          </DialogHeader>
          {result && !proofMode && (
            <>
              <ScannedPackageDetail qr={result.qr} pkg={result.package} />
              <div className="grid grid-cols-2 gap-2 mt-4 sticky bottom-0 bg-white dark:bg-slate-950 pt-2">
                <Button
                  data-testid="rider-cancel-btn"
                  variant="outline"
                  onClick={closeDialog}
                  disabled={submitting}
                  className="h-12 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                >
                  <XIcon className="w-4 h-4 mr-2" /> Cancel
                </Button>
                <Button
                  data-testid="rider-advance-btn"
                  onClick={onAdvance}
                  disabled={submitting}
                  className="h-12 bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-bold uppercase tracking-wider"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                  )}
                  {result.next_action_label}
                </Button>
              </div>
            </>
          )}
          {result && proofMode && (
            <DeliveryProofCapture
              defaultReceiverName={result.package?.receiver_name || ""}
              busy={submitting}
              onCancel={() => setProofMode(false)}
              onConfirm={onProofConfirm}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function prettify(s) {
  return (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}
